/**
 * File des actions préparées hors ligne (notes, paiements, inscriptions).
 *
 * Chaque action garde sa clé d'envoi (son `id`) : renvoyée après une coupure, le serveur ne l'enregistre
 * qu'une fois. L'envoi part au retour du réseau, dans l'ordre de saisie. Une action refusée par le serveur
 * (ou un doublon possible) passe « à vérifier » : l'utilisateur la corrige, la force ou la supprime.
 */
import type { ReceiptData } from "@/utils/receipt";
import { idb } from "./idb";
import { getCacheOwner } from "./session";
import { getOfflineStatus, setForcedOffline, setOfflineStatus, subscribeOfflineStatus } from "./status";

export type OutboxKind = "grades" | "receipt" | "enrollment";

export interface OutboxItem {
  /** Sert aussi de clé d'envoi (Idempotency-Key). */
  id: string;
  userId: string;
  kind: OutboxKind;
  /** Libellé affiché (« Paiement — DIALLO Aïssatou, 150 000 GNF »). */
  label: string;
  createdAt: number;
  payload: unknown;
  /** Une nouvelle saisie remplace l'action en attente de même clé (ex. notes d'une même matière). */
  replaceKey?: string;
  status: "pending" | "review";
  /** Raison du refus ou du doute (« à vérifier »). */
  message?: string;
  /** Envoyer malgré un doublon possible (choix de l'utilisateur). */
  force?: boolean;
  /** Paiement : reçu provisoire remis au guichet, réimprimable tant que l'action n'est pas envoyée. */
  provisional?: ReceiptData;
}

/** Résultat d'une action gardée sur l'appareil, avec le reçu provisoire s'il y a eu un paiement. */
export interface QueuedOffline {
  queued: true;
  provisional: ReceiptData | null;
}

/** Ce que renvoie l'envoi d'une action : faite, ou à vérifier (doublon possible) avec une explication. */
export type ProcessResult = { done: true } | { done: false; review: string };
type Processor = (item: OutboxItem) => Promise<ProcessResult>;

const processors = new Map<OutboxKind, Processor>();

/** Déclare comment envoyer un type d'action (appelé par les services concernés). */
export function registerProcessor(kind: OutboxKind, processor: Processor): void {
  processors.set(kind, processor);
}

/**
 * Nouvelle clé d'envoi. Donnée dès le premier essai en ligne puis reprise par la file : si la réponse se
 * perd après l'enregistrement, le renvoi ne crée pas de doublon.
 */
export function newActionKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Référence d'un reçu provisoire : le numéro officiel ne peut venir que du serveur. */
export function provisionalRef(key: string): string {
  return `PROV-${key.replace(/[^a-z0-9]/gi, "").slice(0, 6).toUpperCase()}`;
}

/** Note du reçu officiel : elle garde la référence du reçu provisoire qu'il remplace. */
export function provisionalNote(note: string | undefined, ref: string): string {
  const mention = `Remplace le reçu provisoire ${ref}`;
  const kept = note?.trim().slice(0, 255 - mention.length - 3);
  return kept ? `${kept} — ${mention}` : mention;
}

/** Options d'un renvoi depuis la file (clé d'envoi de l'action). */
export const replayOptions = (item: OutboxItem) => ({ idempotencyKey: item.id, replay: true });

/** Actions de l'utilisateur connecté, de la plus ancienne à la plus récente. */
export async function listOutbox(): Promise<OutboxItem[]> {
  const owner = getCacheOwner();
  return (await idb.all<OutboxItem>("outbox"))
    .filter((item) => item.userId === owner)
    .sort((a, b) => a.createdAt - b.createdAt);
}

async function refreshCounts(): Promise<void> {
  const items = await listOutbox();
  setOfflineStatus({
    pending: items.filter((i) => i.status === "pending").length,
    review: items.filter((i) => i.status === "review").length,
  });
}

/** Met une action en file ; elle partira dès que le serveur répondra. */
export async function enqueue(
  input: Pick<OutboxItem, "kind" | "label" | "payload" | "replaceKey" | "provisional"> & { id?: string },
): Promise<void> {
  const owner = getCacheOwner();
  if (!owner) throw new Error("Session hors ligne indisponible : reconnectez-vous avec le réseau.");
  if (input.replaceKey) {
    const previous = (await listOutbox()).find((i) => i.replaceKey === input.replaceKey && i.status === "pending");
    if (previous) await idb.delete("outbox", previous.id);
  }
  const item: OutboxItem = { ...input, id: input.id ?? newActionKey(), userId: owner, createdAt: Date.now(), status: "pending" };
  await idb.set("outbox", item.id, item);
  await refreshCounts();
}

export async function removeFromOutbox(id: string): Promise<void> {
  await idb.delete("outbox", id);
  await refreshCounts();
}

/** Remet une action « à vérifier » en attente (`force` : envoyer malgré le doublon possible), puis envoie. */
export async function retryOutbox(id: string, force = false): Promise<void> {
  const item = await idb.get<OutboxItem>("outbox", id);
  if (!item) return;
  await idb.set("outbox", id, { ...item, status: "pending", message: undefined, force: force || item.force });
  await refreshCounts();
  await syncOutbox();
}

let syncing: Promise<void> | null = null;

/** Envoie les actions en attente, dans l'ordre ; s'arrête à la première coupure. */
export function syncOutbox(): Promise<void> {
  if (syncing) return syncing;
  // Mode hors ligne choisi : l'envoi attend « Repasser en ligne ».
  if (getOfflineStatus().forced) return Promise.resolve();
  syncing = (async () => {
    setOfflineStatus({ syncing: true });
    try {
      for (const item of await listOutbox()) {
        if (item.status !== "pending") continue;
        const processor = processors.get(item.kind);
        if (!processor) continue;
        try {
          const result = await processor(item);
          if (!("review" in result)) await idb.delete("outbox", item.id);
          else await idb.set("outbox", item.id, { ...item, status: "review", message: result.review });
        } catch (error) {
          // Coupure : on réessaiera au retour du réseau. Refus du serveur : à vérifier par l'utilisateur.
          if ((error as { status?: number }).status === 0) break;
          const message = error instanceof Error ? error.message : "Envoi refusé par le serveur.";
          await idb.set("outbox", item.id, { ...item, status: "review", message });
        }
      }
    } finally {
      await refreshCounts();
      setOfflineStatus({ syncing: false });
      syncing = null;
    }
  })();
  return syncing;
}

/** Quitte le mode hors ligne choisi : envoie les actions gardées, puis recharge les données à jour. */
export async function goOnline(): Promise<void> {
  setForcedOffline(false);
  await syncOutbox();
  window.location.reload();
}

let started = false;

/** Lance l'envoi automatique : au retour du réseau, au retour en ligne de l'application et toutes les 30 s. */
export function startOutboxSync(): void {
  void refreshCounts().then(() => syncOutbox());
  if (started) return;
  started = true;
  window.addEventListener("online", () => void syncOutbox());
  let wasOnline = getOfflineStatus().online;
  subscribeOfflineStatus(() => {
    const { online } = getOfflineStatus();
    if (online && !wasOnline) void syncOutbox();
    wasOnline = online;
  });
  window.setInterval(() => {
    if (getOfflineStatus().pending > 0) void syncOutbox();
  }, 30_000);
}
